/**
 * Berechnungen für die Diagramme.
 *
 * Getrennt von den Komponenten, damit sie ohne Browser prüfbar sind -
 * eine falsch skalierte Achse fällt sonst erst am fertigen Bild auf.
 */

/**
 * Pfad eines Balkens: oben abgerundet, unten eckig.
 *
 * Ein oben wie unten abgerundeter Balken sieht so aus, als schwebe er über
 * der Grundlinie, und verfälscht damit den Vergleich der Längen.
 */
export function barPath(x: number, y: number, width: number, height: number, radius = 4): string {
  if (height <= 0) return '';
  const r = Math.min(radius, width / 2, height);

  return [
    `M ${x} ${y + height}`,
    `L ${x} ${y + r}`,
    `Q ${x} ${y} ${x + r} ${y}`,
    `L ${x + width - r} ${y}`,
    `Q ${x + width} ${y} ${x + width} ${y + r}`,
    `L ${x + width} ${y + height}`,
    'Z',
  ].join(' ');
}

/**
 * Achsenteilung mit runden Zahlen.
 *
 * Eine Achse, die bei 1.237,50 endet, ist nicht ablesbar - gesucht ist der
 * nächste runde Schritt darüber. Der Höchstwert liegt immer auf einem
 * Teilstrich, damit der längste Balken nicht über die oberste Gitterlinie
 * hinausragt.
 */
export function niceScale(maxValue: number, targetTicks = 4): { max: number; ticks: number[] } {
  if (!Number.isFinite(maxValue) || maxValue <= 0) return { max: 100, ticks: [0, 50, 100] };

  const rough = maxValue / targetTicks;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;

  // Der jeweils *nächstgelegene* runde Schritt, nicht der nächstgrößere:
  // Bei einem Rohwert von 2,2 ist 2 die bessere Teilung als 5 - sonst
  // bleiben von vier gewünschten Teilstrichen nur zwei übrig und das
  // Diagramm verliert seine Ablesbarkeit.
  const step = (normalized < 1.5 ? 1 : normalized < 3 ? 2 : normalized < 7 ? 5 : 10) * magnitude;
  const max = Math.ceil(maxValue / step) * step;

  const ticks: number[] = [];
  // Der Vergleich mit halbem Schritt fängt die Ungenauigkeit auf, die beim
  // wiederholten Addieren von Gleitkommazahlen entsteht.
  for (let value = 0; value <= max + step / 2; value += step) {
    ticks.push(Math.round(value));
  }

  return { max, ticks };
}

/** Cent -> kurze Achsenbeschriftung: 12.500.000 -> "125 Tsd." */
export function shortAmount(cent: number): string {
  const euro = cent / 100;

  if (Math.abs(euro) >= 1_000_000) {
    return `${(euro / 1_000_000).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Mio.`;
  }
  if (Math.abs(euro) >= 1000) {
    return `${(euro / 1000).toLocaleString('de-DE', { maximumFractionDigits: 0 })} Tsd.`;
  }
  return euro.toLocaleString('de-DE', { maximumFractionDigits: 0 });
}

/** Veränderung in Prozent; null, wenn kein sinnvoller Vergleich möglich ist. */
export function changePercent(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}
